import { Box, Button, Modal } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Input } from "../../hookFormComponents.tsx/Input";
import { api } from "../../api/api";
import SelectM from "../../hookFormComponents.tsx/Select";
import { useEffect, useState } from "react";

type Props = {
  modalConfig: { open: boolean; data: any };
  handleClose: () => void;
  setModalConfig: React.Dispatch<
    React.SetStateAction<
      | {
          data?: any;
          open: boolean;
        }
      | undefined
    >
  >;
  fetcher: () => Promise<void>;
};

function CreateModal({
  modalConfig,
  handleClose,
  fetcher,
  setModalConfig,
}: Props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "white",
    boxShadow: 24,
    borderRadius: "10px",
    p: 4,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Required"),
    description: Yup.string(),
    price: Yup.number().typeError("Must be number").required("Required"),
    service_id: Yup.number().typeError("Must be number").required("Required"),
    service_qty: Yup.number()
      .typeError("Must be number")
      .required("Required"),
    period_days: Yup.number().typeError("Must be number").nullable(),
    // Children are minimal slots on this package — only the qty (how many sessions that
    // added family member/friend gets) is collected; everything else is filled in server-side.
    children: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().nullable(),
          service_qty: Yup.number()
            .typeError("Must be number")
            .required("Required")
            .min(1, "Min 1"),
        })
      )
      .default([]),
  });

  const [serviceOptions, setServiceOptions] = useState([]);

  const getServices = () => {
    api.get("/services?limit=1000").then((res) =>
      setServiceOptions(
        res.data.services.map((service: any) => ({
          value: service.id,
          label: service.name,
        }))
      )
    );
  };

  useEffect(() => {
    getServices();
  }, []);

  const {
    formState: { errors },
    control,
    setValue,
    register,
    handleSubmit,
  } = useForm<Yup.InferType<typeof validationSchema>>({
    resolver: yupResolver(validationSchema),
    defaultValues: modalConfig.data
      ? {
          name: modalConfig.data.name,
          description: modalConfig.data.description,
          price: modalConfig.data.price,
          service_id: modalConfig.data.service_id,
          service_qty: modalConfig.data.service_qty,
          period_days: modalConfig.data.period_days,
          children: (modalConfig.data.children || []).map((child: any) => ({
            id: child.id,
            service_qty: child.service_qty,
          })),
        }
      : { children: [] },
  });

  // keyName avoids RHF's default behavior of overwriting each item's "id" with its own
  // generated React key — we need the real "id" (existing child row vs. new) to reach the
  // submit body untouched so the API can tell an update from an insert.
  const { fields: childFields, append: appendChild, remove: removeChild } = useFieldArray({
    control,
    name: "children",
    keyName: "fieldKey",
  });

  return (
    <Modal open={modalConfig.open} onClose={handleClose}>
      <Box sx={style}>
        <form
          noValidate
          onSubmit={handleSubmit((values) => {
            if (modalConfig.data) {
              api
                .post(`/packages/${modalConfig.data.id}/update`, values)
                .then((res) => {
                  fetcher();
                  setModalConfig((prev) => ({
                    data: undefined,
                    open: false,
                  }));
                })
                .catch((e) => console.log(e));
            } else {
              api
                .post(`/packages/create`, values)
                .then((res) => {
                  fetcher();
                  setModalConfig((prev) => ({
                    data: undefined,
                    open: false,
                  }));
                })
                .catch((e) => console.log(e));
            }
          })}
        >
          <Input
            required
            {...register("name")}
            label={"Name"}
            type="text"
            error={errors?.name?.message}
          />
          <Input
            {...register("description")}
            label={"Description"}
            type="text"
            error={errors?.description?.message}
          />
          <Input
            required
            {...register("price")}
            label={"Price"}
            type="text"
            error={errors?.price?.message}
          />

          <Controller
            control={control}
            name="service_id"
            render={({ field }) => (
              <SelectM
                label="Service"
                required
                value={
                  field.value || field.value === 0
                    ? field.value?.toString()
                    : ""
                }
                name={field.name}
                setValue={setValue}
                errors={errors}
                options={serviceOptions}
              />
            )}
          />

          <Input
            required
            {...register("service_qty")}
            label={"Session Quantity"}
            type="text"
            error={errors?.service_qty?.message}
          />
          <Input
            {...register("period_days")}
            label={"Validity (days)"}
            type="text"
            error={errors?.period_days?.message}
          />

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Child Packages</span>
              <Button
                type="button"
                size="small"
                onClick={() => appendChild({ service_qty: 1 } as any)}
              >
                Add Child
              </Button>
            </Box>

            {childFields.map((field, index) => (
              <Box
                key={field.fieldKey}
                sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}
              >
                <Input
                  required
                  {...register(`children.${index}.service_qty` as const)}
                  label={`Child ${index + 1} - Qty`}
                  type="text"
                  error={errors?.children?.[index]?.service_qty?.message}
                />
                <Button
                  type="button"
                  color="error"
                  onClick={() => removeChild(index)}
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "right", mt: 2 }}>
            <Button type="submit" variant="contained">
              Submit
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}

export default CreateModal;
